import * as zod from 'zod';
import SectionHeader from "../CoreComponents/Details/SectionHeader";
import { splitOnCapitals } from '../../Helpers/Functions';
import FormInput from '../CoreComponents/ReactFormComponents/FormInput';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useCallback } from 'react';
import { useScrollLock, useEscapeKey } from '../../hooks/useScrollLock';

type TProps = {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: TaskFormType) => Promise<void>;
    initialData?: TaskFormType | null;
    mode?: 'add' | 'edit';
}

const schema = zod.object({
    taskHeader: zod.string().optional(),
    taskDescription: zod.string().min(1, { message: "Please enter the task description" }),
    order: zod.coerce.number().min(0)
});

export type TaskFormType = zod.infer<typeof schema>;

export default function AddTaskModal({ isOpen, onClose, onSubmit, initialData, mode = 'add' }: TProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<TaskFormType>({
        resolver: zodResolver(schema),
        defaultValues: {
            taskHeader: "",
            taskDescription: "",
            order: 0
        }
    });

    // Reset form with initial data when modal opens or initialData changes
    useEffect(() => {
        if (isOpen && initialData) {
            reset(initialData);
        } else if (isOpen) {
            reset({ taskHeader: "", taskDescription: "", order: 0 });
        }
    }, [isOpen, initialData, reset]);

    const handleCloseModal = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    useEscapeKey(isOpen, handleCloseModal);
    useScrollLock(isOpen);

    const handleFormSubmit = async (data: TaskFormType) => {
        try {
            await onSubmit(data);
        } catch (error) {
            console.error("Error adding task:", error);
        }
    };

    if (!isOpen) return null;

    return (
        <div 
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-modal-title"
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        >
            <div className="bg-white dark:bg-gray-800 dark:text-white rounded-lg shadow-xl w-full max-w-screen-md p-6">
                <div className="mt-4 mb-8">
                    <SectionHeader title={mode === 'edit' ? "Edit Task" : "Add New Task"} id="task-modal-title" />
                </div>

                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    <FormInput
                        dataTestId="task-header-input"
                        required={false}
                        errors={errors}
                        defaultValue=""
                        inputKey="taskHeader"
                        registeredProps={register('taskHeader')}
                        label={splitOnCapitals("TaskHeader")}
                        placeholder="Task Header"
                    />
                    <FormInput
                        dataTestId="task-description-input"
                        required={true}
                        errors={errors}
                        defaultValue=""
                        inputKey="taskDescription"
                        registeredProps={register('taskDescription')}
                        label={splitOnCapitals("TaskDescription")}
                        placeholder="Task Description"
                    />
                    <FormInput
                        dataTestId="task-order-input"
                        required={true}
                        errors={errors}
                        defaultValue={1}
                        inputKey="order"
                        registeredProps={register('order')}
                        label="Order"
                        placeholder="Order (e.g., 1, 2, 3)"
                        type="number"
                    />

                    <div className='flex justify-between mt-16'>
                        <button
                            type="button"
                            data-testid="add-task-form-cancel-button"
                            onClick={handleCloseModal}
                            className='mt-3 text-gray-900 bg-gray-500 dark:text-gray-900 dark:bg-gray-500'
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            data-testid="add-task-form-submit-button"
                            className='text-white bg-orange-1000 dark:text-gray-800 dark:bg-orange-1000'
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}